"use client";

import { Info } from "lucide-react";
import type { Agent } from "@/lib/api";

const SESSION_MIN = 3;
const SESSION_MAX = 30;

function clampSessionMinutes(v: unknown, fallback = 15): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(SESSION_MIN, Math.min(SESSION_MAX, Math.round(n)));
}

function clampWarnMinutes(v: unknown, sessionMax: number, fallback = 1): number {
  const maxLead = Math.max(1, clampSessionMinutes(sessionMax, 15) - 1);
  const n = Number(v);
  const base = Number.isFinite(n) ? Math.round(n) : fallback;
  return Math.max(1, Math.min(maxLead, base));
}

function SliderRow({
  label,
  value,
  min,
  max,
  step = 1,
  unit,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit: string;
  onChange: (n: number) => void;
}) {
  return (
    <label className="agent-limits-slider">
      <div className="agent-limits-slider-head">
        <b>{label}</b>
        <span className="agent-limits-range">
          {min}–{max} {unit}
        </span>
      </div>
      <div className="agent-limits-slider-row">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-label={label}
        />
        <div className="agent-limits-num">
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") return;
              onChange(Number(raw));
            }}
            onBlur={(e) => {
              const n = Number(e.target.value);
              if (!Number.isFinite(n)) onChange(min);
              else onChange(Math.max(min, Math.min(max, Math.round(n))));
            }}
            aria-label={`${label} value`}
          />
          <em>{unit === "minutes" ? "min" : unit === "seconds" ? "sec" : unit}</em>
        </div>
      </div>
    </label>
  );
}

type Props = {
  draft: Agent;
  onChange: (next: Agent) => void;
};

export function AgentEditSessionLimitsPanel({ draft, onChange }: Props) {
  const sessionMax = clampSessionMinutes(draft.sessionMaxMinutes, 15);
  const warnMax = Math.max(1, sessionMax - 1);
  const warn = clampWarnMinutes(draft.sessionWarnMinutes, sessionMax, 1);

  function patch(partial: Partial<Agent>) {
    onChange({ ...draft, ...partial });
  }

  function setSessionMax(next: number) {
    const max = clampSessionMinutes(next, 15);
    patch({
      sessionMaxMinutes: max,
      sessionWarnMinutes: clampWarnMinutes(draft.sessionWarnMinutes, max, 1),
    });
  }

  return (
    <div className="agent-conv agent-limits">
      <section className="agent-limits-card">
        <header className="agent-limits-card-head">
          <div>
            <h3 className="agent-conv-h">Hard limit</h3>
            <p className="agent-limits-lead">
              Control the maximum session length and when to warn the user before ending.
            </p>
          </div>
        </header>

        <SliderRow
          label="Session timeout"
          value={sessionMax}
          min={SESSION_MIN}
          max={SESSION_MAX}
          unit="minutes"
          onChange={setSessionMax}
        />
        <SliderRow
          label="Warn before end"
          value={warn}
          min={1}
          max={warnMax}
          unit="minutes"
          onChange={(n) => patch({ sessionWarnMinutes: clampWarnMinutes(n, sessionMax, 1) })}
        />

        <div className="agent-limits-note" role="note">
          <Info size={15} strokeWidth={2} aria-hidden />
          <p>
            At T−warn the agent speaks a short warning. At T=0 it finishes the current playout,
            then speaks goodbye and ends the session — never mid-phrase.
          </p>
        </div>
      </section>
    </div>
  );
}
